import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { type GridColDef } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton } from '../../../../components/common';
import { useEjercicioStore } from '../store/ejercicioStore';
import { EjercicioService } from '../services/ejercicioService';
import type { EjercicioContable, PeriodoContable } from '../types/ejercicio';
import { PERIODO_LABELS } from '../types/ejercicio';

const EjerciciosPage: React.FC = () => {
  const {
    ejercicios,
    loading,
    error,
    fetchEjercicios,
    clearError,
  } = useEjercicioStore();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newAnio, setNewAnio] = useState(new Date().getFullYear());
  const [periodosDialogOpen, setPeriodosDialogOpen] = useState(false);
  const [selectedEjercicio, setSelectedEjercicio] = useState<EjercicioContable | null>(null);
  const [periodos, setPeriodos] = useState<PeriodoContable[]>([]);
  const [loadingPeriodos, setLoadingPeriodos] = useState(false);

  useEffect(() => {
    fetchEjercicios();
  }, [fetchEjercicios]);

  const columns: GridColDef[] = [
    { field: 'anio', headerName: 'Año', width: 120 },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'ABIERTO' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleViewPeriodos(params.row)}
            title="Ver Periodos"
          >
            <ViewIcon fontSize="small" />
          </IconButton>
          {params.row.estado === 'ABIERTO' && (
            <IconButton
              size="small"
              color="warning"
              onClick={() => handleCerrarEjercicio(params.row.idEjercicio)}
              title="Cerrar Ejercicio"
            >
              <LockIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleCreateEjercicio = async () => {
    try {
      await EjercicioService.create(newAnio);
      await fetchEjercicios();
      setCreateDialogOpen(false);
    } catch (err) {
      // Error handled by errorService
    }
  };

  const handleCerrarEjercicio = async (id: number) => {
    if (window.confirm('¿Está seguro de cerrar este ejercicio? Esta acción no se puede deshacer.')) {
      try {
        await EjercicioService.cerrar(id);
        await fetchEjercicios();
      } catch (err) {
        // Error handled by errorService
      }
    }
  };

  const handleViewPeriodos = async (ejercicio: EjercicioContable) => {
    setSelectedEjercicio(ejercicio);
    setLoadingPeriodos(true);
    setPeriodosDialogOpen(true);
    try {
      const data = await EjercicioService.getPeriodos(ejercicio.idEjercicio);
      setPeriodos(data);
    } catch (err) {
      // Error handled by errorService
    } finally {
      setLoadingPeriodos(false);
    }
  };

  const handleCerrarPeriodo = async (idPeriodo: number) => {
    try {
      await EjercicioService.cerrarPeriodo(idPeriodo);
      if (selectedEjercicio) {
        const data = await EjercicioService.getPeriodos(selectedEjercicio.idEjercicio);
        setPeriodos(data);
      }
    } catch (err) {
      // Error handled
    }
  };

  const handleAbrirPeriodo = async (idPeriodo: number) => {
    try {
      await EjercicioService.abrirPeriodo(idPeriodo);
      if (selectedEjercicio) {
        const data = await EjercicioService.getPeriodos(selectedEjercicio.idEjercicio);
        setPeriodos(data);
      }
    } catch (err) {
      // Error handled
    }
  };

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Ejercicios Contables"
        subtitle="Gestión de ejercicios y periodos contables"
        actions={
          <AddButton text="Nuevo Ejercicio" onClick={() => setCreateDialogOpen(true)} />
        }
      />

      {loading && <CircularProgress />}
      {error && <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && (
        <DataGrid
          columns={columns}
          rows={ejercicios}
          getRowId={(row: EjercicioContable) => row.idEjercicio}
        />
      )}

      {/* Create Ejercicio Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Crear Nuevo Ejercicio</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Año"
            type="number"
            value={newAnio}
            onChange={(e) => setNewAnio(parseInt(e.target.value))}
            sx={{ mt: 2 }}
            inputProps={{ min: 2000, max: 2100 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Se crearán automáticamente 15 periodos (00-Apertura, 01-12 Meses, 13-Cierre Resultados, 14-Cierre Balance)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleCreateEjercicio} variant="contained">Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Periodos Dialog */}
      <Dialog open={periodosDialogOpen} onClose={() => setPeriodosDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Periodos del Ejercicio {selectedEjercicio?.anio}
        </DialogTitle>
        <DialogContent>
          {loadingPeriodos ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>N°</TableCell>
                    <TableCell>Periodo</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha Inicio</TableCell>
                    <TableCell>Fecha Fin</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periodos.map((periodo) => (
                    <TableRow key={periodo.idPeriodo}>
                      <TableCell>{String(periodo.numeroPeriodo).padStart(2, '0')}</TableCell>
                      <TableCell>{PERIODO_LABELS[periodo.numeroPeriodo] || `Periodo ${periodo.numeroPeriodo}`}</TableCell>
                      <TableCell>
                        <Chip
                          label={periodo.estado}
                          color={periodo.estado === 'ABIERTO' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{periodo.fechaInicio ? new Date(periodo.fechaInicio).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>{periodo.fechaFin ? new Date(periodo.fechaFin).toLocaleDateString() : '-'}</TableCell>
                      <TableCell align="center">
                        {periodo.estado === 'ABIERTO' ? (
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleCerrarPeriodo(periodo.idPeriodo)}
                            title="Cerrar Periodo"
                          >
                            <LockIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleAbrirPeriodo(periodo.idPeriodo)}
                            title="Abrir Periodo"
                          >
                            <LockOpenIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeriodosDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </ListLayoutPanel>
  );
};

export default EjerciciosPage;
